import type { RxDocument } from 'rxdb';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Alert } from '@flintmint/web-shared/components/alert';
import { Button } from '@flintmint/web-shared/components/button';
import { NoData, type NoDataProps } from '@flintmint/web-shared/components/no-data';

import { getUserSourceErrorMessage } from '@/components/context-item';
import { useItemActions } from '@/hooks/use-item-actions';
import { useResyncUserSource } from '@/hooks/use-resync-user-source';

import type { UserSource } from '@flintmint/core-shared/rxdb';

import styles from '@/components/editor/editor.module.scss';

export const CortexProcessing = (props: CortexProcessingProps) => {
  const { userSource } = props;

  const { configureDestructiveOptions } = useItemActions();
  const { resync, isResyncing } = useResyncUserSource(userSource);

  const processingFailed = userSource?.syncStatus === 'error';

  const sharedProps: NoDataProps = {
    color: 'darker',
    className: styles.processingView,
    icon: <Icon size={48} multiColor type="ai-brain-02" />,
  };

  if (isResyncing) {
    return <NoData {...sharedProps} description={`We are processing the content!\nPlease come back in couple of minutes!`} />;
  }

  if (processingFailed) {
    const destructiveActions = configureDestructiveOptions([{ ...userSource!.toJSON(), type: 'userSources' }]);

    return (
      <NoData
        {...sharedProps}
        description={getUserSourceErrorMessage(userSource?.error, 'long') ?? `Something went wrong\nwhile processing the source.`}
        actionRenderer={() => (
          <>
            {destructiveActions.map((action) => (
              <Alert
                key={action.name}
                {...action.deleteData!}
                trigger={
                  <Button color={action.danger ? 'danger' : 'dark'} appearance="greyscale">
                    {action.name}
                  </Button>
                }
              />
            ))}
            <Button color="dark" loading={isResyncing} disabled={isResyncing} onClick={resync}>
              Try again
            </Button>
          </>
        )}
      />
    );
  }

  return null;
};

export interface CortexProcessingProps {
  userSource?: RxDocument<UserSource>;
}
